import { Link } from "@/lib/router-compat";
import {
  ShieldCheck,
  Phone,
  ArrowRight,
  CheckCircle2,
  Clock,
  Brain,
} from "lucide-react";
import { SaintEligibilityCheck } from "@/components/SaintEligibilityCheck";

export const SaintHero = () => (
  <section className="relative overflow-hidden text-primary-foreground">
    {/* Base image — optimized WebP srcset (was a 2MB PNG with no srcset) */}
    <div className="absolute inset-0">
      <img
        src="https://vibe.filesafe.space/1787327131284421342/assets/75f94f83-f4b1-43e7-96a1-c2b58ea8de41.webp"
        srcSet="https://vibe.filesafe.space/1787327131284421342/assets/81c3eac1-592d-4dca-9ed4-5334a7e3a0d6.webp 640w, https://vibe.filesafe.space/1787327131284421342/assets/75f94f83-f4b1-43e7-96a1-c2b58ea8de41.webp 1024w, https://vibe.filesafe.space/1787327131284421342/assets/86b911b2-2001-4734-a952-612e0ef2f8b1.webp 1600w, https://vibe.filesafe.space/1787327131284421342/assets/642d0cf8-4748-4826-8528-886fbd8585dc.webp 2400w"
        sizes="100vw"
        alt="Private SAINT® TMS treatment suite with specialized Localite neuronavigation chair and targeting equipment at Neuro Wellness TMS Centers of America in Coral Springs, FL"
        className="h-full w-full object-cover"
        loading="eager"
        fetchPriority="high"
        width={2400}
        height={1374}
      />
    </div>

    {/* Layered gradient wash — deep navy with directional light */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary-deep/97 via-primary-deep/92 to-primary-deep/80" />
    <div className="absolute inset-0 bg-gradient-to-t from-primary-deep via-transparent to-primary-deep/60" />
    <div className="absolute inset-0 bg-gradient-to-r from-primary-deep/95 via-primary-deep/55 to-transparent" />

    {/* Subtle dot-grid texture */}
    <div
      className="absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }}
    />

    {/* Glowing accent orbs */}
    <div className="pointer-events-none absolute -right-24 -top-16 h-[28rem] w-[28rem] rounded-full bg-accent/20 blur-[100px]" />
    <div className="pointer-events-none absolute -left-32 top-1/3 h-96 w-96 rounded-full bg-medium-blue/25 blur-[90px]" />
    <div className="pointer-events-none absolute right-1/4 bottom-0 h-72 w-72 rounded-full bg-accent/10 blur-[80px]" />

    {/* Diagonal accent beam */}
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-1/2 left-1/2 h-[200%] w-40 -translate-x-1/2 rotate-12 bg-gradient-to-b from-accent/10 via-accent/5 to-transparent blur-2xl" />
    </div>

    {/* Decorative circuit lines — bottom corners */}
    <svg
      className="pointer-events-none absolute bottom-0 left-0 h-48 w-48 opacity-20 text-accent"
      viewBox="0 0 200 200"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 190 L10 120 L60 120 L60 80 L110 80"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="10" cy="190" r="3" fill="currentColor" />
      <circle cx="60" cy="120" r="3" fill="currentColor" />
      <circle cx="110" cy="80" r="4" fill="currentColor" />
      <path d="M110 80 L160 80" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="160" cy="80" r="3" fill="currentColor" />
    </svg>
    <svg
      className="pointer-events-none absolute bottom-0 right-0 h-48 w-48 opacity-20 text-medium-blue"
      viewBox="0 0 200 200"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M190 190 L190 130 L140 130 L140 90 L90 90"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="190" cy="190" r="3" fill="currentColor" />
      <circle cx="140" cy="130" r="3" fill="currentColor" />
      <circle cx="90" cy="90" r="4" fill="currentColor" />
      <path d="M90 90 L40 90" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="40" cy="90" r="3" fill="currentColor" />
    </svg>

    {/* Top edge accent line */}
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />

    <div className="container-px relative mx-auto max-w-6xl px-4 py-12 sm:py-16 lg:py-20">
      <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
        {/* Left: headline + trust points (desktop) / second on mobile */}
        <div className="order-2 lg:order-1 text-center lg:text-left">
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
            <Link
              to="/find-a-verified-saint-provider"
              className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-xs font-semibold text-accent backdrop-blur-sm transition-all hover:bg-accent/25 hover:border-accent sm:text-sm"
            >
              <ShieldCheck className="h-4 w-4" />
              Verified SAINT® Provider · Coral Springs, Florida
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <div className="inline-flex items-center rounded-xl bg-white px-3 py-1 shadow-sm border border-border">
              <img
                src="https://vibe.filesafe.space/1787327131284421342/attachments/861c17b1-d72f-46dc-9f82-4d1ea308284e.png"
                alt="SAINT by Magnus Medical official logo"
                className="h-6 sm:h-7 w-auto object-contain"
                width={140}
                height={35}
                loading="eager"
              />
            </div>
          </div>

          <h1 className="mt-5 font-headings text-[2rem] font-extrabold leading-[1.08] text-white drop-shadow-lg sm:text-4xl md:text-5xl lg:text-[2.75rem]">
            Depression relief in five days,
            <span className="block text-gradient-accent">not six weeks</span>
          </h1>

          <p className="mt-4 text-sm font-semibold text-white/95 drop-shadow sm:text-base">
            <span className="text-accent">Who SAINT® is for:</span> adults with
            major depressive disorder who have tried at least two
            antidepressants without adequate improvement.
          </p>

          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/90 drop-shadow sm:text-lg lg:mx-0">
            SAINT® is an FDA-cleared, fMRI-guided, accelerated form of TMS
            developed at Stanford. Find out if you're a candidate — it takes two
            minutes.
          </p>

          {/* Compact trust points */}
          <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2.5 lg:justify-start">
            {[
              { icon: CheckCircle2, text: "FDA-cleared" },
              { icon: Clock, text: "5 days, not 6 weeks" },
              { icon: Brain, text: "fMRI-guided targeting" },
            ].map((item) => (
              <div
                key={item.text}
                className="flex items-center gap-2 rounded-lg bg-white/5 px-2.5 py-1 backdrop-blur-sm border border-white/10"
              >
                <item.icon className="h-4 w-4 text-accent" />
                <span className="text-xs font-semibold text-white sm:text-sm">
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 lg:hidden">
            <a
              href="tel:9548279793"
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-95"
            >
              <Phone className="h-4 w-4" />
              Call 954-827-9793
            </a>
          </div>

          <div className="mt-6 hidden justify-start lg:flex">
            <a
              href="tel:9548279793"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 underline decoration-accent/50 underline-offset-4 hover:text-white"
            >
              <Phone className="h-4 w-4 text-accent" />
              Prefer to call? 954-827-9793
            </a>
          </div>
        </div>

        {/* Right: eligibility form — the conversion target (first on mobile) */}
        <div className="order-1 lg:order-2 lg:pl-6">
          <SaintEligibilityCheck
            source="saint-hero"
            variant="card"
            redirectTo="/thank-you-saint"
          />
        </div>
      </div>
    </div>

    {/* Bottom fade into next section */}
    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />
  </section>
);
